import { lazy, Suspense } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
// auth
import { AuthGuard, RoleBasedGuard } from 'src/auth/guard';
// layouts
import DashboardLayout from 'src/layouts/dashboard';
// components
import { LoadingScreen } from 'src/components/loading-screen';
//
import { Authorities } from 'src/utils/authorities';

// ----------------------------------------------------------------------

const OverviewPage = lazy(() => import('src/pages/dashboard/overview'));

const UserListPage = lazy(() => import('src/pages/users/userlist'));
const UserNewPage = lazy(() => import('src/pages/users/usercreate'));
const UserEditPage = lazy(() => import('src/pages/users/userEdit'));

const RoleListPage = lazy(() => import('src/pages/roles/rolelist'));
const RoleNewPage = lazy(() => import('src/pages/roles/rolecreate'));
const RoleEditPage = lazy(() => import('src/pages/roles/roleedit'));

const PermissionListPage = lazy(() => import('src/pages/permissions/permissions'));
const SettingsPage = lazy(() => import('src/pages/settings/settings'));

const SupplierListPage = lazy(() => import('src/pages/suppliers/supplierlist'));
const SupplierNewPage = lazy(() => import('src/pages/suppliers/suppliercreate'));
const SupplierEditPage = lazy(() => import('src/pages/suppliers/supplieredit'));
const SupplierDetailPage = lazy(() => import('src/pages/suppliers/supplierdetail'));

const ClientListPage = lazy(() => import('src/pages/clients/clientlist'));
const ClientNewPage = lazy(() => import('src/pages/clients/clientcreate'));
const ClientEditPage = lazy(() => import('src/pages/clients/clientedit'));
const ClientDetailPage = lazy(() => import('src/pages/clients/clientdetail'));

const ServiceListPage = lazy(() => import('src/pages/services/servicelist'));
const ServiceNewPage = lazy(() => import('src/pages/services/servicecreate'));
const ServiceEditPage = lazy(() => import('src/pages/services/serviceedit'));
const ServiceDetailPage = lazy(() => import('src/pages/services/servicedetail'));

const QuotationListPage = lazy(() => import('src/pages/quotations/quotationlist'));
const QuotationNewPage = lazy(() => import('src/pages/quotations/quotationcreate'));
const QuotationEditPage = lazy(() => import('src/pages/quotations/quotationedit'));
const QuotationDetailPage = lazy(() => import('src/pages/quotations/quotationdetail'));

const ContractListPage = lazy(() => import('src/pages/contracts/contractlist'));
const ContractNewPage = lazy(() => import('src/pages/contracts/contractcreate'));
const ContractEditPage = lazy(() => import('src/pages/contracts/contractedit'));
const ContractDetailPage = lazy(() => import('src/pages/contracts/contractdetail'));

const IncomeListPage = lazy(() => import('src/pages/incomes/incomelist'));
const IncomeNewPage = lazy(() => import('src/pages/incomes/incomecreate'));
const IncomeEditPage = lazy(() => import('src/pages/incomes/incomeedit'));
const IncomeDetailPage = lazy(() => import('src/pages/incomes/incomedetail'));

// ----------------------------------------------------------------------

/** Envolve um elemento na barreira de permissao com o ecra de acesso negado. */
const guarded = (permissions: string[], element: React.ReactNode) => (
  <RoleBasedGuard permissions={permissions} hasContent>
    {element}
  </RoleBasedGuard>
);

export const dashboardRoutes = [
  {
    path: 'dashboard',
    element: (
      <AuthGuard>
        <DashboardLayout>
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      </AuthGuard>
    ),
    children: [
      { index: true, element: <OverviewPage /> },
      {
        path: 'users',
        children: [
          { path: 'list', element: guarded([Authorities.READ_USERS], <UserListPage />) },
          { path: 'new', element: guarded([Authorities.CREATE_USERS], <UserNewPage />) },
          { path: ':id/edit', element: guarded([Authorities.UPDATE_USERS], <UserEditPage />) },
        ],
      },
      {
        path: 'roles',
        children: [
          { path: 'list', element: guarded([Authorities.READ_ROLES], <RoleListPage />) },
          { path: 'new', element: guarded([Authorities.CREATE_ROLES], <RoleNewPage />) },
          { path: ':id/edit', element: guarded([Authorities.UPDATE_ROLES], <RoleEditPage />) },
        ],
      },
      {
        path: 'clients',
        children: [
          { index: true, element: <Navigate to="list" replace /> },
          { path: 'list', element: guarded([Authorities.READ_CLIENTS], <ClientListPage />) },
          { path: 'new', element: guarded([Authorities.CREATE_CLIENTS], <ClientNewPage />) },
          { path: ':id/edit', element: guarded([Authorities.UPDATE_CLIENTS], <ClientEditPage />) },
          { path: ':id', element: guarded([Authorities.READ_CLIENTS], <ClientDetailPage />) },
        ],
      },
      {
        path: 'suppliers',
        children: [
          { index: true, element: <Navigate to="list" replace /> },
          { path: 'list', element: guarded([Authorities.READ_SUPPLIERS], <SupplierListPage />) },
          { path: 'new', element: guarded([Authorities.CREATE_SUPPLIERS], <SupplierNewPage />) },
          { path: ':id/edit', element: guarded([Authorities.UPDATE_SUPPLIERS], <SupplierEditPage />) },
          { path: ':id', element: guarded([Authorities.READ_SUPPLIERS], <SupplierDetailPage />) },
        ],
      },
      {
        path: 'services',
        children: [
          { index: true, element: <Navigate to="list" replace /> },
          { path: 'list', element: guarded([Authorities.READ_SERVICES], <ServiceListPage />) },
          { path: 'new', element: guarded([Authorities.CREATE_SERVICES], <ServiceNewPage />) },
          { path: ':id/edit', element: guarded([Authorities.UPDATE_SERVICES], <ServiceEditPage />) },
          { path: ':id', element: guarded([Authorities.READ_SERVICES], <ServiceDetailPage />) },
        ],
      },
      {
        path: 'quotations',
        children: [
          { index: true, element: <Navigate to="list" replace /> },
          { path: 'list', element: guarded([Authorities.READ_QUOTATIONS], <QuotationListPage />) },
          { path: 'new', element: guarded([Authorities.CREATE_QUOTATIONS], <QuotationNewPage />) },
          { path: ':id/edit', element: guarded([Authorities.UPDATE_QUOTATIONS], <QuotationEditPage />) },
          { path: ':id', element: guarded([Authorities.READ_QUOTATIONS], <QuotationDetailPage />) },
        ],
      },
      {
        path: 'contracts',
        children: [
          { index: true, element: <Navigate to="list" replace /> },
          { path: 'list', element: guarded([Authorities.READ_CONTRACTS], <ContractListPage />) },
          { path: 'new', element: guarded([Authorities.CREATE_CONTRACTS], <ContractNewPage />) },
          { path: ':id/edit', element: guarded([Authorities.UPDATE_CONTRACTS], <ContractEditPage />) },
          { path: ':id', element: guarded([Authorities.READ_CONTRACTS], <ContractDetailPage />) },
        ],
      },
      {
        path: 'incomes',
        children: [
          { index: true, element: <Navigate to="list" replace /> },
          { path: 'list', element: guarded([Authorities.READ_INCOMES], <IncomeListPage />) },
          { path: 'new', element: guarded([Authorities.CREATE_INCOMES], <IncomeNewPage />) },
          { path: ':id/edit', element: guarded([Authorities.UPDATE_INCOMES], <IncomeEditPage />) },
          { path: ':id', element: guarded([Authorities.READ_INCOMES], <IncomeDetailPage />) },
        ],
      },
      {
        path: 'permissions',
        children: [
          {
            path: 'list',
            element: guarded([Authorities.READ_PERMISSIONS], <PermissionListPage />),
          },
        ],
      },
      {
        path: 'settings',
        element: guarded([Authorities.READ_SETTINGS], <SettingsPage />),
      },
    ],
  },
];
